import { Line } from '@react-three/drei'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Ruler3D, TicksConfig } from 'features/assetViz/objects/decor/Ruler3D'
import { HorizontalWeldFeature3D } from 'features/assetViz/objects/features/HorizontalWeldFeature3D'
import { NozzleFeature3D } from 'features/assetViz/objects/features/NozzleFeature3D'
import { PolygonalPlateFeature3D } from 'features/assetViz/objects/features/PolygonalPlateFeature3D'
import { DeckBittFeature3D } from 'features/assetViz/objects/features/shipDeck/DeckBittFeature3D'
import { DeckChockFeature3D } from 'features/assetViz/objects/features/shipDeck/DeckChockFeature3D'
import { DeckHatchFeature3D } from 'features/assetViz/objects/features/shipDeck/DeckHatchFeature3D'
import { VerticalWeldFeature3D } from 'features/assetViz/objects/features/VerticalWeldFeature3D'
import { getDeckGeometry } from 'features/assetViz/utils/geometryDefines'
import { Vec3 } from 'features/assetViz/utils/helpers'
import {
  overlayMaterialDefines,
  RenderLayerID,
  RenderOrder,
} from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoShipAssetModel,
  GeckoShipDeck,
} from 'types'

import { Surface3D, Surface3DGroupProps } from './Surface3D'

/**
 * A surface representing a ship deck (such as a flight deck)
 * @param model               The "ship deck" component model
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */

export const RichShipDeckSurface3D: React.FC<
  { model: GeckoShipDeck } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { assetModel, clipConfig } = useAssetVizInternal()

  const {
    bounds,
    nozzles,
    verticalWelds,
    horizontalWelds,
    polygonalPlates,
    bitts,
    chocks,
    hatches,
  } = React.useMemo(() => {
    return {
      bounds: model.componentBounds,
      nozzles: model.nozzlesArray,
      verticalWelds: model.verticalWeldsArray,
      horizontalWelds: model.horizontalWeldsArray,
      polygonalPlates: model.polygonalPlatesArray,
      bitts: model.bittsArray,
      chocks: model.chocksArray,
      hatches: model.hatchesArray,
    }
  }, [model])

  const { bottomStart, bottomEnd, topStart, topEnd } = React.useMemo(() => {
    const widthDifference = model.startWidth
      .minus(model.endWidth)
      .abs()
      .scale(0.5)

    const bottomStart =
      model.startWidth.num >= model.endWidth.num
        ? model.componentBounds.x.start
        : model.componentBounds.x.start.plus(widthDifference)

    const bottomEnd =
      model.startWidth.num >= model.endWidth.num
        ? model.componentBounds.x.end
        : model.componentBounds.x.end.minus(widthDifference)

    const topStart =
      model.endWidth.num > model.startWidth.num
        ? model.componentBounds.x.start
        : model.componentBounds.x.start.plus(widthDifference)

    const topEnd =
      model.endWidth.num > model.startWidth.num
        ? model.componentBounds.x.end
        : model.componentBounds.x.end.minus(widthDifference)

    return {
      bottomStart,
      bottomEnd,
      topStart,
      topEnd,
    }
  }, [model.startWidth, model.endWidth, model.componentBounds])

  const geometry = React.useMemo(() => {
    if (
      model.length.unit !== GeckoMeasurementUnit.Meters ||
      model.startWidth.unit !== GeckoMeasurementUnit.Meters ||
      model.endWidth.unit !== GeckoMeasurementUnit.Meters
    )
      throw new Error(
        `Ship deck's length, end width, and start width are not all in meters.`
      )

    return getDeckGeometry(
      model.length.num,
      model.startWidth.num,
      model.endWidth.num
    )
  }, [model.length, model.startWidth, model.endWidth])

  const positionXBottomTick = React.useCallback<
    (coord: GeckoMeasurement) => Vec3
  >(
    (coord) => {
      const relX = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(model.componentBounds.x.start).num
      return [relX, 0, 0] // offset ruler by -1
    },
    [model.componentBounds]
  )
  const positionXTopTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(model.componentBounds.x.start).num
      return [relX, model.length.num, 0] // offset ruler by -1
    },
    [model.componentBounds, model.length]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      let relY = coord.num
      if (
        assetModel instanceof GeckoShipAssetModel &&
        !!(assetModel as GeckoShipAssetModel).data.frame_size
      ) {
        const frameSizeToPreferredUnit = (
          assetModel as GeckoShipAssetModel
        ).data.frame_size!.convertTo(model.componentBounds.start.y.unit)
        relY =
          coord.num * frameSizeToPreferredUnit.num -
          model.componentBounds.start.y.num
      } else {
        relY = coord
          .convertTo(GeckoMeasurementUnit.Meters)
          .minus(model.componentBounds.start.y).num
      }
      return [model.maxWidth.scale(0.5).num, relY, 0] // offset ruler by -1
    },
    [model.maxWidth, assetModel, model.componentBounds]
  )

  const yTicksConfig = React.useMemo(() => {
    if (
      !(assetModel instanceof GeckoShipAssetModel) ||
      !(assetModel as GeckoShipAssetModel).data.frame_size
    )
      return undefined

    const start = model.componentBounds.start.y
    const end = model.componentBounds.end.y
    const measurementUnit = GeckoMeasurementUnit.Frames
    const intervalSize = 2
    const tickStart = start.convertToFrames(assetModel.data.frame_size!)
    const tickEnd = end.convertToFrames(assetModel.data.frame_size!)
    const tickInterval = Math.sign(end.minus(start).num) * intervalSize
    return {
      measurementUnit,
      includeStart: true,
      includeEnd: true,
      includeTicks: true,
      tickInterval,
      tickStart,
      tickEnd,
    } as TicksConfig
  }, [assetModel, model.componentBounds])

  const centerLinePoints = React.useMemo(
    () => [
      model.maxWidth.scale(0.5).num,
      0,
      0,
      model.maxWidth.scale(0.5).num,
      model.length.num,
      0,
    ],
    [model.maxWidth, model.length]
  )

  return (
    <Surface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}>
      <Line
        layers={RenderLayerID.VisibleNonCollidable}
        renderOrder={RenderOrder.Markup}
        points={centerLinePoints}
        linewidth={2}
        dashed={false}
        dashScale={5}
        color={'grey'}
        clippingPlanes={clipConfig.planes}
        {...overlayMaterialDefines}
      />
      <Ruler3D
        anchor={'center'}
        start={model.componentBounds.start.y}
        end={model.componentBounds.end.y}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionYTick}
        fullTicksConfig={yTicksConfig}
      />
      <Ruler3D
        anchor={'top'}
        start={bottomStart}
        end={bottomEnd}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionXBottomTick}
      />
      <Ruler3D
        anchor={'bottom'}
        start={topStart}
        end={topEnd}
        includeTicks
        includeStart
        includeEnd
        getTickPosition={positionXTopTick}
      />
      {nozzles.map((nozzle) => (
        <NozzleFeature3D key={nozzle.id} model={nozzle} />
      ))}
      {verticalWelds.map((weld) => (
        <VerticalWeldFeature3D key={weld.id} model={weld} />
      ))}
      {horizontalWelds.map((weld) => (
        <HorizontalWeldFeature3D key={weld.id} model={weld} />
      ))}
      {bitts.map((bitt) => (
        <DeckBittFeature3D key={bitt.id} model={bitt} />
      ))}
      {chocks.map((chock) => (
        <DeckChockFeature3D key={chock.id} model={chock} />
      ))}
      {hatches.map((hatch) => (
        <DeckHatchFeature3D key={hatch.id} model={hatch} />
      ))}
      {polygonalPlates.map((plate) => (
        <PolygonalPlateFeature3D key={plate.id} model={plate} />
      ))}
    </Surface3D>
  )
}
